import * as Sentry from '@sentry/react'
import { isError } from 'utils/js'

import { fromWsClientSubscription } from './from-ws-client-subscription'

export type Variables = Record<string, unknown> | undefined
type Callback<D> = (data: D | null) => unknown
type Query = string
type Key = string

const cache = new Map<Key, AsyncIterableIterator<unknown>>()
const cacheClearTimeouts = new Map<Key, NodeJS.Timeout>()
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const callbacks = new Map<Key, Callback<any>[]>()

export function getOrMakeSubscription<D, V extends Variables>({
  query,
  callback,
  variables,
  token,
  subscriptionKey,
}: {
  query: Query
  callback?: Callback<D>
  variables?: V
  token: string
  subscriptionKey: string
}) {
  const key = getKey(query, variables)

  clearTimeout(cacheClearTimeouts.get(key))

  if (!cache.get(key)) {
    createSubscription(query, variables, token, subscriptionKey)
  }

  if (callback) {
    const current = callbacks.get(key)
    const next = current ? [...current, callback] : [callback]
    callbacks.set(key, next)
  }

  return () => {
    const current = callbacks.get(key) ?? []
    const next = callback && current ? current.filter(cb => cb !== callback) : []

    if (callback) {
      callbacks.set(key, next)
    }

    if (next.length === 0) {
      cleanUpSubscription(key)
    }
  }
}

function createSubscription(
  query: Query,
  variables: Variables,
  token: string,
  subscriptionKey: string,
) {
  const key = getKey(query, variables)

  const iterator = fromWsClientSubscription({
    query,
    variables,
    token,
    subscriptionKey,
  })

  ;(async () => {
    try {
      for await (const payload of iterator) {
        const current = callbacks.get(key)
        if (current) {
          for (const fn of current) {
            fn(payload.data)
          }
        }
      }
    } catch (iteratorError) {
      handleIteratorError(iteratorError, subscriptionKey, query, variables)
    }
  })()

  cache.set(key, iterator)

  callbacks.set(key, [])
}

function cleanUpSubscription(key: Key) {
  clearTimeout(cacheClearTimeouts.get(key))

  const timeout = setTimeout(() => {
    try {
      const iterator = cache.get(key)
      iterator?.return?.()
      cache.delete(key)
      // oxlint-disable-next-line unicorn/catch-error-name
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(
        '[use-subscription] Did not unsubscribe iterator',
        isError(e) ? e.message : String(e),
      )
      cache.delete(key)
    }
  }, 1000)

  cacheClearTimeouts.set(key, timeout)
}

function getKey(query: Query, variables?: Variables) {
  return JSON.stringify({ query, variables })
}

/**
 * WebSocket close codes that are considered normal and shouldn't be reported as errors
 * Based on RFC 6455 and common WebSocket usage patterns
 */
const NORMAL_CLOSE_CODES = new Set([
  1000, // Normal closure
  1001, // Going away (e.g., browser tab closing)
  1005, // No status received
  1006, // Abnormal closure (but not necessarily an error)
  1009, // Message too big
  1013, // Try again later (temporary server issue)
  1014, // Bad gateway (temporary network issue)
])

/**
 * Checks if an error is a normal WebSocket close event that shouldn't be reported
 */
function isNormalCloseEvent(error: unknown) {
  if (error && typeof error === 'object' && 'code' in error && typeof error.code === 'number') {
    return NORMAL_CLOSE_CODES.has(error.code)
  }
  return false
}

/**
 * Checks if an error is a generic WebSocket error event (not a close event)
 * These are normal connection events that shouldn't be reported
 */
function isGenericWebSocketError(error: unknown) {
  return Boolean(
    error &&
    typeof error === 'object' &&
    'type' in error &&
    (error as { type: unknown }).type === 'error' &&
    'target' in error &&
    'currentTarget' in error,
  )
}

/**
 * Determines if a WebSocket iterator error should be reported
 */
function shouldReportIteratorError(error: unknown) {
  // Don't report normal close events
  if (isNormalCloseEvent(error)) {
    const code = (error as { code: number }).code
    // eslint-disable-next-line no-console
    console.log(`WebSocket subscription closed with normal code ${code}`)
    return false
  }

  // Don't report generic WebSocket error events
  if (isGenericWebSocketError(error)) {
    // eslint-disable-next-line no-console
    console.log('WebSocket subscription generic error event', error)
    return false
  }

  return true
}

/**
 * Handles errors from the WebSocket subscription iterator
 */
function handleIteratorError(
  error: unknown,
  subscriptionKey: string,
  query: Query,
  variables: Variables,
): void {
  if (!shouldReportIteratorError(error)) {
    return
  }

  const sentryError = new Error(
    `WebSocket subscription iterator error: ${isError(error) ? error.message : String(error)}`,
    {
      cause: 'websocket iterator',
    },
  )

  Sentry.captureException(sentryError, {
    tags: {
      component: 'websocket-subscription',
      subscriptionKey,
    },
    extra: {
      query,
      variables,
      subscriptionKey,
      originalError: error,
    },
  })
}
